// @givmo/app-admin — pending join-request COUNT (EP-SUITE2), for the dashboard's Needs-attention block.
//
// A count-only read. The dashboard surfaces that requests are waiting and links to Admin Team, which
// owns the actual inbox (<JoinRequestsSection>) — the dashboard never adjudicates a request, so it has
// no business rendering one.
//
// DOUBLE-GATED, and the gate is the interesting part: `GET /admin/company/join-requests` 404s while the
// backend's `COMPANY_JOIN_OR_CREATE_ENABLED` flag is off AND 403s a Member (the service's
// `company_admin:add` gate). So the caller must gate on the web switch + the Owner role BEFORE enabling
// this query, and a FAILED read must never render as "0 pending" — a Member or a flag-off environment
// seeing a confident zero would be a lie about state it is not allowed to observe. Hence
// `pendingCount` is `null` (= unknown, show nothing) rather than 0 whenever the read has not succeeded.

import { useQuery } from '@tanstack/react-query';
import { sdk, type CompanyJoinRequestOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { isPendingRequest } from './join-requests-decode';

/** One page is enough for a count; a full page means "at least this many" (see `atLeast`). */
const COUNT_PAGE_LIMIT = 50;

export const JOIN_REQUESTS_PENDING_COUNT_KEY = [
  'companies-list_company_join_requests',
  'pending-count',
] as const;

export interface PendingJoinRequests {
  /** Pending requests on the first page, or `null` while unknown (pending / disabled / failed read). */
  pendingCount: number | null;
  /**
   * True when the page came back FULL, so the real total may be higher and the count must be rendered
   * as "N+". A count-only read does not paginate to exhaustion, and the api-client middleware strips
   * `meta` from the envelope, so there is no total to read either — "N+" is the honest form.
   */
  atLeast: boolean;
}

/**
 * Read the pending join-request count. Pass `enabled: false` (flag off, or the caller is not an Owner)
 * and the query never fires — the surface then shows nothing at all, which is the correct rendering of
 * "not observable from here".
 */
export function usePendingJoinRequests(enabled: boolean): PendingJoinRequests {
  const query = useQuery({
    queryKey: JOIN_REQUESTS_PENDING_COUNT_KEY,
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await sdk.companiesListCompanyJoinRequests({
        query: { 'page[limit]': COUNT_PAGE_LIMIT, 'page[offset]': 0 },
        signal,
        throwOnError: true,
      });
      return extractList<CompanyJoinRequestOut>(data);
    },
  });

  if (!query.isSuccess) return { pendingCount: null, atLeast: false };
  return {
    pendingCount: query.data.filter(isPendingRequest).length,
    atLeast: query.data.length >= COUNT_PAGE_LIMIT,
  };
}
